import { InputWithLabel } from '@adotel/components'
import { LazyQueryExecFunction, OperationVariables } from '@apollo/client'
import { Button, Grow, Typography } from '@material-ui/core'
import { FC, useEffect, useRef, useState } from 'react'

import AmountSlider, { AmountRange } from '../../components/SliderWithLabel'
import TimePeriodForm from '../../components/TimePeriodForm'
import { DateObject } from '../../helpers/date'
import { getTranslate } from '../../localization'
import { SortBy } from '../../types/walletEnums'
import {
  PaymentTransactionFilterInput,
  PaymentTransactionResponse
} from '../../types/walletTransaction'
import useStyles from './styles/SettlementFilterBox.styles'

type SettlementFilterBoxProps = {
  filters: PaymentTransactionFilterInput
  setfilters: (v: PaymentTransactionFilterInput) => void
  setPage: (v: number) => void
  getTransactions: LazyQueryExecFunction<
    {
      settlementTransactions: PaymentTransactionResponse
    },
    OperationVariables
  >
  loadingForTransactions: boolean
  sortBy: SortBy
  startJob: LazyQueryExecFunction<any, OperationVariables>
  setwaitingForWebSocketToCalcOutStandingBalance: (
    value: React.SetStateAction<boolean>
  ) => void
}

export const SettlementFilterBox: FC<SettlementFilterBoxProps> = ({
  filters,
  setfilters,
  getTransactions,
  loadingForTransactions,
  setPage,
  sortBy,
  startJob,
  setwaitingForWebSocketToCalcOutStandingBalance
}) => {
  const classes = useStyles()
  const [transactionTimeDatePickerIsOpen, settransactionTimeDatePickerIsOpen] =
    useState(false)
  const [checkinDatePickerIsOpen, setcheckinDatePickerIsOpen] = useState(false)
  const [checkoutDatePickerIsOpen, setcheckoutDatePickerIsOpen] =
    useState(false)

  const [transactionTimeDateObject, settransactionTimeDateObject] =
    useState<DateObject>({
      gregorianStartDate: filters.transactionCreationRange?.from || '',
      gregorianEndDate: filters.transactionCreationRange?.to || ''
    })
  const [checkinDateObject, setcheckinDateObject] = useState<DateObject>({
    gregorianStartDate: filters.checkinRange?.from || '',
    gregorianEndDate: filters.checkinRange?.to || ''
  })
  const [checkoutDateObject, setcheckoutDateObject] = useState<DateObject>({
    gregorianStartDate: filters.checkoutRange?.from || '',
    gregorianEndDate: filters.checkoutRange?.to || ''
  })
  const [onDoneClickForCloseDatePicker, setOnDoneClickForCloseDatePicker] =
    useState<boolean>(true)
  const datePickerTransactionTimeRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      if (
        datePickerTransactionTimeRef.current &&
        !datePickerTransactionTimeRef?.current?.contains(
          event.target as Node
        ) &&
        transactionTimeDatePickerIsOpen
      ) {
        settransactionTimeDatePickerIsOpen(false)
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [datePickerTransactionTimeRef, transactionTimeDatePickerIsOpen])
  const datePickerCheckinRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      if (
        datePickerCheckinRef.current &&
        !datePickerCheckinRef?.current?.contains(event.target as Node) &&
        checkinDatePickerIsOpen
      ) {
        setcheckinDatePickerIsOpen(false)
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [datePickerCheckinRef, checkinDatePickerIsOpen])
  const datePickerCheckoutRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      if (
        datePickerCheckoutRef.current &&
        !datePickerCheckoutRef?.current?.contains(event.target as Node) &&
        checkoutDatePickerIsOpen
      ) {
        setcheckoutDatePickerIsOpen(false)
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [datePickerCheckoutRef, checkoutDatePickerIsOpen])
  return (
    <Grow in>
      <div className={classes.root}>
        <div className={classes.filterContainer}>
          <div className={classes.twoColumnRow}>
            <InputWithLabel
              label={getTranslate('Reservation No.')}
              placeholder={getTranslate('Enter Reservation Number')}
              autoComplete="off"
              fullWidth
              value={filters.reservationNumber || ''}
              onChange={(event) =>
                setfilters({
                  ...filters,
                  reservationNumber: event.currentTarget.value
                })
              }
              inputSize="small"
            />
            <AmountSlider
              max={100000}
              min={0}
              onChange={(newValue: AmountRange) =>
                setfilters({
                  ...filters,
                  amountRange: {
                    from: newValue.low,
                    to: newValue.high
                  }
                })
              }
              value={{
                high: filters.amountRange?.to || 100000,
                low: filters.amountRange?.from || 0
              }}
              label={'Amount'}
              currency={getTranslate('IRR')}
            />
          </div>
          <div className={classes.threeColumnRow}>
            <div ref={datePickerTransactionTimeRef}>
              <Typography>{getTranslate('Transaction Time')}</Typography>
              <TimePeriodForm
                left
                isOpen={transactionTimeDatePickerIsOpen}
                setTimePickerSelected={settransactionTimeDatePickerIsOpen}
                dateObject={transactionTimeDateObject}
                onChange={(data: DateObject) => {
                  settransactionTimeDateObject(data)
                  setfilters({
                    ...filters,
                    transactionCreationRange: {
                      from: data.gregorianStartDate || '',
                      to: data.gregorianEndDate || ''
                    }
                  })
                }}
                onDoneClickForCloseDatePicker={onDoneClickForCloseDatePicker}
                setOnDoneClickForCloseDatePicker={
                  setOnDoneClickForCloseDatePicker
                }
                enableAllDays
              />
            </div>
            <div ref={datePickerCheckinRef}>
              <Typography>{getTranslate('Check In')}</Typography>
              <TimePeriodForm
                isOpen={checkinDatePickerIsOpen}
                setTimePickerSelected={setcheckinDatePickerIsOpen}
                dateObject={checkinDateObject}
                onChange={(data: DateObject) => {
                  setcheckinDateObject(data)
                  setfilters({
                    ...filters,
                    checkinRange: {
                      from: data.gregorianStartDate || '',
                      to: data.gregorianEndDate || ''
                    }
                  })
                }}
                onDoneClickForCloseDatePicker={onDoneClickForCloseDatePicker}
                setOnDoneClickForCloseDatePicker={
                  setOnDoneClickForCloseDatePicker
                }
                enableAllDays
              />
            </div>
            <div ref={datePickerCheckoutRef}>
              <Typography>{getTranslate('Check Out')}</Typography>
              <TimePeriodForm
                isOpen={checkoutDatePickerIsOpen}
                setTimePickerSelected={setcheckoutDatePickerIsOpen}
                dateObject={checkoutDateObject}
                onChange={(data: DateObject) => {
                  setcheckoutDateObject(data)
                  setfilters({
                    ...filters,
                    checkoutRange: {
                      from: data.gregorianStartDate || '',
                      to: data.gregorianEndDate || ''
                    }
                  })
                }}
                onDoneClickForCloseDatePicker={onDoneClickForCloseDatePicker}
                setOnDoneClickForCloseDatePicker={
                  setOnDoneClickForCloseDatePicker
                }
                enableAllDays
              />
            </div>
          </div>
        </div>
        <div className={classes.ButtonsContainer}>
          <Button
            className={classes.resetButton}
            variant="text"
            color="secondary"
            onClick={() => {
              setfilters({
                ...filters,
                amountRange: undefined,
                checkinRange: undefined,
                checkoutRange: undefined,
                creditorID: undefined,
                page: 1,
                paymentStatuses: undefined,
                reservationNumber: undefined,
                transactionCreationRange: undefined
              })
              setcheckinDateObject({})
              setcheckoutDateObject({})
              settransactionTimeDateObject({})
            }}>
            {getTranslate('Reset')}
          </Button>
          <Button
            className={classes.applyButton}
            variant="contained"
            color="primary"
            disabled={loadingForTransactions}
            onClick={() => {
              getTransactions({
                variables: {
                  filters: filters,
                  sortBy: sortBy
                }
              })
              const {
                page: unusedPage,
                perPage: unusedPerPage,
                ...filtersWithoutPagination
              } = filters

              startJob({
                variables: {
                  filters: filtersWithoutPagination,
                  sortBy
                }
              })
              setwaitingForWebSocketToCalcOutStandingBalance(true)
              setPage(1)
            }}>
            {getTranslate('Apply')}
          </Button>
        </div>
      </div>
    </Grow>
  )
}
